/* eslint-disable jsdoc/require-param */
import React from "react";
import { Grid, Stack, Typography, Paper } from "@mui/material";
import SnapshotTiles from "../../../../components/user-profile/SnapshotTiles";
import QuickActionsCard from "./cards/QuickActionsCard";
import StatusTogglesCard from "./cards/StatusTogglesCard";
import HierarchyCard from "./cards/HierarchyCard";
import FinanceApplicationsCard from "./cards/FinanceApplicationsCard";
import UserInfoCard from "./cards/UserInfoCard";
import LeaderActionsCard from "./cards/LeaderActionsCard";

/**
 * The UserDetailContent component displays the content for the user detail page.
 *
 * It takes the following props:
 * - dealer: The user object containing user details.
 * - bankingDetails: The banking details associated with the dealer.
 * - userSuppliersState: The state of the user's suppliers.
 * - userProductTypesState: The state of the user's product types.
 * - upStream: The user's upstream dealers.
 * - parent: The user's parent dealer.
 * - currentUser: The current authenticated user object.
 * - growthLeader: Whether the user is a growth leader.
 * - isLeader: Whether the user is a leader.
 * - isLeaderBlocked: Whether the user is a blocked leader.
 * - onEditClick: The function to call when the edit button is clicked.
 * - onChangeLeaver: The function to call when the user's leaver status is changed.
 * - onDeleteClick: The function to call when the delete button is clicked.
 * - onChangePasswordClick: The function to call when the change password button is clicked.
 * - onContractsClick: The function to call when the contracts button is clicked.
 * - onAlertClick: The function to call when the alert button is clicked.
 * - onSuppliersClick: The function to call when the suppliers button is clicked.
 * - onChangeParentClick: The function to call when the change parent button is clicked.
 * - onSupportClick: The function to call when the support button is clicked.
 * - onCreditClick: The function to call when the credit button is clicked.
 * - onGrowthLeaderChange: The function to call when the user's growth leader status is changed.
 * - onIsLeaderChange: The function to call when the user's leader status is changed.
 * - onIsLeaderBlockChange: The function to call when the user's blocked leader status is changed.
 *
 * The component displays the user's information in a two-column layout, with the main information panel
 * on the left and the sidebar on the right. The main information panel contains the user's personal
 * information, contact information, address information, business information, and payment information.
 * The sidebar contains the user's status toggles, hierarchy information, and finance applications.
 */
export default function UserDetailContent({
  dealer,
  bankingDetails,
  userSuppliersState,
  userProductTypesState,
  upStream,
  parent,
  currentUser,
  growthLeader,
  isLeader,
  isLeaderBlocked,
  onEditClick,
  onChangeLeaver,
  onDeleteClick,
  onChangePasswordClick,
  onContractsClick,
  onAlertClick,
  onSuppliersClick,
  onChangeParentClick,
  onSupportClick,
  onCreditClick,
  onGrowthLeaderChange,
  onIsLeaderChange,
  onIsLeaderBlockChange,
  onUserTypeClick,
}) {
  if (!dealer.phone) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
        <Typography variant="h6" color="text.secondary">
          User does not exist.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <SnapshotTiles currentUser={dealer} otherProfile />

      <Grid container spacing={3}>
        {/* Main Information Panel */}
        <Grid item xs={12} lg={8}>
          <UserInfoCard
            dealer={dealer}
            currentUser={currentUser}
            onEditClick={onEditClick}
            bankingDetails={bankingDetails}
            userProductTypesState={userProductTypesState}
            userSuppliersState={userSuppliersState}
          />
        </Grid>
        {/* Sidebar */}
        {currentUser?.is_admin ? (
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              <QuickActionsCard
                dealer={dealer}
                currentUser={currentUser}
                onChangeLeaver={onChangeLeaver}
                onDeleteClick={onDeleteClick}
                onChangePasswordClick={onChangePasswordClick}
                onContractsClick={onContractsClick}
                onAlertClick={onAlertClick}
                onSuppliersClick={onSuppliersClick}
                onUserTypeClick={onUserTypeClick}
              />

              <StatusTogglesCard
                dealer={dealer}
                growthLeader={growthLeader}
                isLeader={isLeader}
                isLeaderBlocked={isLeaderBlocked}
                onGrowthLeaderChange={onGrowthLeaderChange}
                onIsLeaderChange={onIsLeaderChange}
                onIsLeaderBlockChange={onIsLeaderBlockChange}
              />

              <HierarchyCard
                parent={parent}
                upStream={upStream}
                dealer={dealer}
                onChangeParentClick={onChangeParentClick}
              />

              <FinanceApplicationsCard
                onSupportClick={onSupportClick}
                onCreditClick={onCreditClick}
              />
            </Stack>
          </Grid>
        ) : (
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              <LeaderActionsCard
                dealer={dealer}
                isLeader={isLeader}
                isLeaderBlocked={isLeaderBlocked}
                onIsLeaderChange={onIsLeaderChange}
                onAlertClick={onAlertClick}
                onSuppliersClick={onSuppliersClick}
              />{" "}
            </Stack>
          </Grid>
        )}
      </Grid>
    </>
  );
}
